import { routes } from "../../assets/constants";
import AuthClientStore from '../services/auth-client.service';
import { ApiMethod } from "../models/api-types.type";
import { useAuthContext } from "./use-auth-context.hook";
import { Product } from "../../modules/vendor/my-products/models/product.model";

export interface CategoryNode {
    _id: string;
    name: string | Record<string, string>;
    slug: string;
    icon?: string;
    image?: string;
    /** True when `image` is a custom upload (vs an auto-generated grid). */
    coverIsCustom?: boolean;
    productCount: number;
    order: number;
    depth: number;
    parentId?: string;
    children: CategoryNode[];
}

export interface CreateCategoryPayload {
    name: string | Record<string, string>;
    slug?: string;
    parentId?: string;
    icon?: string;
    image?: string;
    isActive?: boolean;
    familySlug: string;
}

export interface UpdateCategoryPayload {
    name?: string | Record<string, string>;
    slug?: string;
    icon?: string;
    image?: string;
    isActive?: boolean;
}

export const useCategoriesApi = () => {
    const { sendAuthGuardedRequest } = useAuthContext();

    const getTree = async (familySlug?: string): Promise<CategoryNode[]> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.categories.tree + (familySlug ? '?familySlug=' + familySlug : ''),
        );
        return response?.data || response || [];
    };

    const findAll = async (familySlug?: string): Promise<any[]> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.categories.findAll + (familySlug ? '?familySlug=' + familySlug : ''),
        );
        return response?.data || response || [];
    };

    const create = async (data: CreateCategoryPayload): Promise<CategoryNode> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.categories.create,
            data,
        );
        return response?.data || response;
    };

    const update = async (id: string, data: UpdateCategoryPayload): Promise<CategoryNode> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.PATCH,
            routes.categories.update(id),
            data,
        );
        return response?.data || response;
    };

    const move = async (id: string, newParentId: string | null): Promise<CategoryNode> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.PATCH,
            routes.categories.move(id),
            { newParentId },
        );
        return response?.data || response;
    };

    const remove = async (id: string): Promise<void> => {
        await sendAuthGuardedRequest(
            ApiMethod.DELETE,
            routes.categories.deleteOne(id),
        );
    };

    const assignProducts = async (categoryId: string, productUuids: string[]): Promise<{ modified: number }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.categories.assignProducts(categoryId),
            { productUuids },
        );
        return response?.data || response;
    };

    const removeProducts = async (categoryId: string, productUuids: string[]): Promise<{ modified: number }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.DELETE,
            routes.categories.removeProducts(categoryId),
            { productUuids },
        );
        return response?.data || response;
    };

    const generateCover = async (categoryId: string): Promise<{ image: string | null }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.categories.generateCover(categoryId),
        );
        return response?.data || response || { image: null };
    };

    const uploadCover = async (categoryId: string, file: File): Promise<{ image: string }> => {
        const form = new FormData();
        form.append('cover', file);
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.categories.uploadCover(categoryId),
            form,
        );
        return response?.data || response;
    };

    const removeCover = async (categoryId: string): Promise<{ image: null }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.DELETE,
            routes.categories.removeCover(categoryId),
        );
        return response?.data || response || { image: null };
    };

    const reorder = async (items: { id: string; order: number }[]): Promise<void> => {
        await sendAuthGuardedRequest(
            ApiMethod.PATCH,
            routes.categories.reorder,
            { items },
        );
    };

    const removeAll = async (familySlug?: string): Promise<{ deleted: number }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.DELETE,
            routes.categories.removeAll + (familySlug ? '?familySlug=' + familySlug : ''),
        );
        return response?.data || response || { deleted: 0 };
    };

    const getProducts = async (categoryId: string, page = 1, limit = 500): Promise<{ data: Product[]; total: number; nextPage: number }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.categories.getProducts(categoryId) + `?page=${page}&limit=${limit}`,
        );
        const items = response?.data || [];
        return {
            data: items.map((productData: any) => {
                const product = new Product();
                const prepared = product.prepareProduct(productData);
                (prepared as any).variantCount = productData.variantCount || 0;
                return prepared;
            }),
            total: response?.total || items.length,
            nextPage: response?.nextPage ?? -1,
        };
    };

    const searchProducts = async (params: { page?: number; limit?: number; name?: string; familySlug?: string }): Promise<{ data: Product[]; nextPage: number }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.products.findAll + '/search',
            { page: params.page || 1, limit: params.limit || 30, name: params.name || undefined, ...(params.familySlug ? { familySlug: params.familySlug } : {}) },
        );
        const items = response?.data || [];
        return {
            data: items.map((productData: any) => {
                const product = new Product();
                return product.prepareProduct(productData);
            }),
            nextPage: response?.nextPage ?? -1,
        };
    };

    /**
     * Start the AI agent via SSE. Calls onEvent for each streamed event.
     * Returns an abort controller so the caller can cancel.
     */
    const runAiAgent = (
        params: { mode: 'generate' | 'assign'; familySlug: string; language?: string },
        onEvent: (event: { type: string; data: any }) => void,
        onDone: () => void,
        onError: (err: string) => void,
    ): AbortController => {
        const apiUrl = process.env.REACT_APP_PMS_API_URL as string;
        const token = AuthClientStore.getAccessToken();
        const controller = new AbortController();

        fetch(`${apiUrl}${routes.categories.autoCategorize}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(params),
            signal: controller.signal,
        }).then(async (response) => {
            if (!response.ok) {
                onError(`HTTP ${response.status}: ${response.statusText}`);
                return;
            }
            const reader = response.body?.getReader();
            if (!reader) { onError('No response body'); return; }
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    const trimmed = line.trim();
                    if (!trimmed.startsWith('data: ')) continue;
                    const payload = trimmed.slice(6);
                    if (payload === '[DONE]') { onDone(); return; }
                    try {
                        onEvent(JSON.parse(payload));
                    } catch { /* skip malformed */ }
                }
            }
            onDone();
        }).catch((err) => {
            if (err.name !== 'AbortError') onError(err.message);
        });

        return controller;
    };

    return {
        getTree,
        findAll,
        create,
        update,
        move,
        remove,
        removeAll,
        assignProducts,
        removeProducts,
        reorder,
        getProducts,
        searchProducts,
        runAiAgent,
        generateCover,
        uploadCover,
        removeCover,
    };
};
